import firebase from '../firebase'
import { validate, schemas } from 'validators'
import { logout, login, getCustomToken } from '../request'
import { parseCookies } from 'nookies'
import { signup } from 'utils/auth'

export async function loginWithSession(email, password) {
  // As httpOnly cookies are to be used, do not persist any state client side.
  // firebase.auth().setPersistence(firebase.auth.Auth.Persistence.NONE)
  const { user } = await firebase
    .auth()
    .signInWithEmailAndPassword(email, password)
  const { token, claims } = await user.getIdTokenResult()
  const { csrfToken = '' } = parseCookies()
  await login({ idToken: token, csrfToken })
  // await firebase.auth().signOut()
  return { user, token, claims }
}

export async function signupWithSession(email: string, password: string) {
  const { user } = await signup(email, password)
  const { token, claims } = await user.getIdTokenResult()

  const { csrfToken = '' } = parseCookies()
  await login({ idToken: token, csrfToken })

  return { user, token, claims }
}

export async function loginWithCustomToken(customToken: string) {
  const { user } = await firebase.auth().signInWithCustomToken(customToken)
  const { token, claims } = await user.getIdTokenResult()
  const { csrfToken = '' } = parseCookies()

  if (typeof window !== 'undefined') await login({ idToken: token, csrfToken })

  return { user, token, claims }
}

/**
 * すでに認証してればユーザーを返し、してなければcustomTokenをサーバー側にリクエスト
 * customToken認証する。
 */
export async function authorizeUser() {
  const user = await getUser()
  console.info('authorizeUser:user', user)
  if (user) return user

  const token = await getCustomToken()
  if (!token) return null
  console.info('authorizeUser:customToken', token)
  const userInfo = await firebase.auth().signInWithCustomToken(token)

  return userInfo?.user || null
}

export async function getUser() {
  const user = await new Promise<firebase.User | null>((res, rej) => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
      unsubscribe()
      if (user) {
        await user.reload()
        res(user)
      }
      res(null)
    })
  })

  return user
}

export async function getIdToken() {
  const newToken = await new Promise<string | null>((resolve, reject) => {
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      unsubscribe()
      if (user) resolve(user.getIdToken())
      resolve(null)
    })
  })

  return newToken
}

export async function loginWithEmail(email: string, password: string) {
  return firebase.auth().signInWithEmailAndPassword(email, password)
}

export async function sendPasswordResetEmail(email) {
  return validate({ email }, schemas.resetPassword).then(() =>
    firebase.auth().sendPasswordResetEmail(email)
  )
}

export function signInWithTwitter() {
  const provider = new firebase.auth.TwitterAuthProvider()
  return firebase.auth().signInWithRedirect(provider)
}

export function signInWithApple() {
  const provider = new firebase.auth.OAuthProvider('apple.com')
  return firebase.auth().signInWithRedirect(provider)
}

export function checkIfSignInWithEmail(email: string) {
  return firebase.auth().fetchSignInMethodsForEmail(email)
}

export async function signout() {
  if (typeof window !== 'undefined') await logout()

  await firebase.auth().signOut()
  return
}

export function getRedirectResult() {
  return firebase.auth().getRedirectResult()
}

export function onRefreshIdToken() {
  return firebase.auth().onIdTokenChanged(user => user && user.getIdToken())
}

export async function updateEmail(newEmail: string, password: string) {
  const user = firebase.auth().currentUser
  await getCredentialsWithPassword(user.email, password)

  return await user.updateEmail(newEmail)
}

export async function updatePassword(oldPassword: string, newPassword: string) {
  const user = firebase.auth().currentUser
  await getCredentialsWithPassword(user.email, oldPassword)

  return await user.updatePassword(newPassword)
}

export async function getCredentialsWithPassword(
  oldEmail: string,
  password: string
) {
  const user = firebase.auth().currentUser
  const credentials = firebase.auth.EmailAuthProvider.credential(
    oldEmail,
    password
  )
  return user.reauthenticateWithCredential(credentials)
}

export async function sendEmailVerifycation() {
  const user = firebase.auth().currentUser
  if (!user) return Promise.reject(new Error('no user login'))

  return user.sendEmailVerification()
}

export async function signUp(email: string, password: string) {
  const userCredential = await firebase
    .auth()
    .createUserWithEmailAndPassword(email, password)

  await firebase
    .firestore()
    .collection('users')
    .doc(userCredential.user.uid)
    .set({ email })

  await userCredential.user.sendEmailVerification()

  return userCredential
}

/**
 * 将来的にcustomClaims内に格納するように変更
 */
export async function checkIfProfileRegistered(): Promise<boolean> {
  const user = firebase.auth().currentUser
  if (!user) return false

  const userSnap = await firebase
    .firestore()
    .collection('users')
    .doc(user.uid)
    .get()

  return !!userSnap.get('isRegisteredProfile')
}
